import { useFormContext } from "react-hook-form";
import { CreditClientFormData } from "./schema";
import { Table } from "@/app/entities/TableCredit";
import { formatDate } from "@/app/utils/formatDate";
import { ArrowRight, Banknote } from "lucide-react";
import { useState } from "react";
import { formatCpfNumber } from "@/app/utils/cpfFormater";
import { useMutation } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { useToast } from "@/view/components/ui/use-toast";
import { createPublicCustomerBody } from "@/app/services/selfHireService/createPublicCustomer";
import { createPublicTransactionBody } from "@/app/services/selfHireService/createPublicTransaction";
import InstallmentsTax from "../../InstallmentsTax";
import { selfHireService } from "@/app/services/selfHireService";
import { useLeadContext } from "@/app/contexts/LeadContext";
import { parse } from "date-fns";

interface ReviewStepProps {
  visible: boolean;
  table: Table;
  cpf: string;
  name: string;
  birthDate: string;
  onBack: () => void;
}

export function ReviewStep({
  visible,
  table,
  cpf,
  name,
  birthDate,
  onBack,
}: ReviewStepProps) {
  const { linkName } = useParams();
  const { watch } = useFormContext<CreditClientFormData>();
  const [showInstallments, setShowInstallments] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const formData = watch();
  const { leadId } = useLeadContext();

  const { mutate: createCustomer, isPending: isCreatingCustomer } = useMutation(
    {
      mutationFn: async (data: createPublicCustomerBody) =>
        await selfHireService.createPublicCustomer(data),
      onSuccess: (customer) => {
        createTransaction({
          value: table.ValorSolicitado ? table.ValorSolicitado * 100 : 0,
          customerId: customer._id,
          CodigoTabela: table.CodigoTabela ?? "",
          CodigoProduto: table.CodigoProduto ?? 0,
          CodigoEmpregador: table.CodigoEmpregador ?? 0,
          CodigoOrg: table.CodigoOrgao ?? 0,
          Prazo: table.Prazo ?? 0,
          linkName: linkName!,
        });
      },
      onError: (error) => {
        console.error("Erro ao criar cliente:", error);
        toast({
          variant: "destructive",
          title: "Erro ao fazer o saque",
          description:
            "Não foi possível fazer o saque. Verifique os dados e tente novamente.",
        });
      },
    },
  );

  const { mutate: createTransaction, isPending: isCreatingTransaction } =
    useMutation({
      mutationFn: async (data: createPublicTransactionBody) =>
        await selfHireService.createPublicTransaction(data),
      onSuccess: (transaction) => {
        navigate(`/withdraw-success?code=${transaction.contract}`);
      },
      onError: (error) => {
        console.error("Erro ao criar transação:", error);
        toast({
          variant: "destructive",
          title: "Erro ao fazer o saque",
          description:
            "Não foi possível fazer o saque. Tente novamente mais tarde.",
        });
      },
    });

  const parseDate = (str: string) => {
    return parse(str, "dd/MM/yyyy", new Date());
  };

  const handleSubmit = async () => {
    const data = {
      name,
      cpf: cpf.replace(/\D/g, ""),
      birthDate: parseDate(birthDate).toISOString(),
      email: formData.personalInfo.email,
      phone: formData.personalInfo.phone.replace(/\D/g, ""),
      leadId: leadId as string,
      rg: {
        ...formData.personalInfo.rg,
        number: formData.personalInfo.rg.number.replace(/\D/g, ""),
        date: parseDate(formData.personalInfo.rg.date).toISOString(),
      },
      address: {
        ...formData.address,
        zipCode: formData.address.zipCode.replace(/\D/g, ""),
        number: formData.address.number || "",
      },
      bankAccount: formData.bankAccount,
    };

    try {
      createCustomer(data);
    } catch (error) {
      console.error("Erro ao enviar formulário:", error);
    }
  };

  if (!visible) return null;

  return (
    <div className="flex min-h-screen w-full flex-col items-center justify-center">
      <div className="flex w-full max-w-xl items-center justify-center bg-white02 px-8 py-10">
        <div className="w-full">
          <div className="mb-6 flex items-center justify-center gap-2 rounded-lg border border-gray01 bg-gray02 py-3 text-center text-sm font-semibold tracking-wide sm:text-lg">
            <p>{formatCpfNumber(cpf)}</p> <p className="uppercase">{name}</p>
          </div>
          <InstallmentsTax
            currentTable={table}
            onAlterarClick={() => setShowInstallments(!showInstallments)}
          />

          <div className="mb-6">
            <div className="rounded-xl bg-gray02 px-5 py-4">
              <h2 className="mb-4 text-base font-semibold text-black01">
                Meus dados
              </h2>

              <div className="mb-6">
                <h3 className="mb-3 text-base font-semibold text-black03">
                  Informações pessoais
                </h3>
                <div className="space-y-2">
                  <div className="flex items-center">
                    <span className="text-xs font-semibold text-black03 sm:text-sm">
                      Nome
                    </span>
                    <div className="mx-2 flex-1 border-b border-dotted border-black04"></div>
                    <span className="text-xs font-semibold text-black03 sm:text-sm">
                      {name}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-xs font-semibold text-black03 sm:text-sm">
                      CPF
                    </span>
                    <div className="mx-2 flex-1 border-b border-dotted border-black04"></div>
                    <span className="text-xs font-semibold text-black03 sm:text-sm">
                      {cpf
                        ? cpf.replace(
                            /(\d{3})(\d{3})(\d{3})(\d{2})/,
                            "$1.$2.$3-$4",
                          )
                        : ""}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-xs font-semibold text-black03 sm:text-sm">
                      Data de nascimento
                    </span>
                    <div className="mx-2 flex-1 border-b border-dotted border-black04"></div>
                    <span className="text-xs font-semibold text-black03 sm:text-sm">
                      {formatDate(birthDate)}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-xs font-semibold text-black03 sm:text-sm">
                      E-mail
                    </span>
                    <div className="mx-2 flex-1 border-b border-dotted border-black04"></div>
                    <span className="text-xs font-semibold text-black03 sm:text-sm">
                      {formData.personalInfo.email}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-xs font-semibold text-black03 sm:text-sm">
                      Telefone
                    </span>
                    <div className="mx-2 flex-1 border-b border-dotted border-black04"></div>
                    <span className="text-xs font-semibold text-black03 sm:text-sm">
                      {formData.personalInfo.phone}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-xs font-semibold text-black03 sm:text-sm">
                      RG
                    </span>
                    <div className="mx-2 flex-1 border-b border-dotted border-black04"></div>
                    <span className="text-xs font-semibold text-black03 sm:text-sm">
                      {formData.personalInfo.rg.number}
                    </span>
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <h3 className="mb-3 text-base font-semibold text-black01">
                  Endereço
                </h3>
                <div className="space-y-2">
                  <div className="flex items-center">
                    <span className="text-xs font-semibold text-black03 sm:text-sm">
                      CEP
                    </span>
                    <div className="mx-2 flex-1 border-b border-dotted border-black04"></div>
                    <span className="text-xs font-semibold text-black03 sm:text-sm">
                      {formData.address.zipCode}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-xs font-semibold text-black03 sm:text-sm">
                      Endereço
                    </span>
                    <div className="mx-2 flex-1 border-b border-dotted border-black04"></div>
                    <span className="text-xs font-semibold text-black03 sm:text-sm">
                      {formData.address.street}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-xs font-semibold text-black03 sm:text-sm">
                      Número
                    </span>
                    <div className="mx-2 flex-1 border-b border-dotted border-black04"></div>
                    <span className="text-xs font-semibold text-black03 sm:text-sm">
                      {formData.address.number}
                    </span>
                  </div>
                  {formData.address.complement && (
                    <div className="flex items-center">
                      <span className="text-xs font-semibold text-black03 sm:text-sm">
                        Complemento
                      </span>
                      <div className="mx-2 flex-1 border-b border-dotted border-black04"></div>
                      <span className="text-xs font-semibold text-black03 sm:text-sm">
                        {formData.address.complement}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center">
                    <span className="text-xs font-semibold text-black03 sm:text-sm">
                      Bairro
                    </span>
                    <div className="mx-2 flex-1 border-b border-dotted border-black04"></div>
                    <span className="text-xs font-semibold text-black03 sm:text-sm">
                      {formData.address.neighborhood}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-xs font-semibold text-black03 sm:text-sm">
                      Cidade
                    </span>
                    <div className="mx-2 flex-1 border-b border-dotted border-black04"></div>
                    <span className="text-xs font-semibold text-black03 sm:text-sm">
                      {formData.address.city}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-xs font-semibold text-black03 sm:text-sm">
                      UF
                    </span>
                    <div className="mx-2 flex-1 border-b border-dotted border-black04"></div>
                    <span className="text-xs font-semibold text-black03 sm:text-sm">
                      {formData.address.state}
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="mb-3 text-base font-semibold text-black01">
                  Informações bancárias
                </h3>
                <div className="space-y-2">
                  <div className="flex items-center">
                    <span className="text-xs font-semibold text-black03 sm:text-sm">
                      Tipo de conta
                    </span>
                    <div className="mx-2 flex-1 border-b border-dotted border-black04"></div>
                    <span className="text-xs font-semibold text-black03 sm:text-sm">
                      {formData.bankAccount.accountType === "CC"
                        ? "Conta Corrente"
                        : "Poupança"}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-xs font-semibold text-black03 sm:text-sm">
                      Banco
                    </span>
                    <div className="mx-2 flex-1 border-b border-dotted border-black04"></div>
                    <span className="text-xs font-semibold text-black03 sm:text-sm">
                      {formData.bankAccount.bank}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-xs font-semibold text-black03 sm:text-sm">
                      Agência
                    </span>
                    <div className="mx-2 flex-1 border-b border-dotted border-black04"></div>
                    <span className="text-xs font-semibold text-black03 sm:text-sm">
                      {formData.bankAccount.agency}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-xs font-semibold text-black03 sm:text-sm">
                      Conta
                    </span>
                    <div className="mx-2 flex-1 border-b border-dotted border-black04"></div>
                    <span className="text-xs font-semibold text-black03 sm:text-sm">
                      {formData.bankAccount.account}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mb-6">
            <button
              className="flex w-full items-center gap-3 rounded-xl border border-primary bg-primary/10 px-6 py-5 hover:opacity-70"
              disabled={isCreatingCustomer || isCreatingTransaction}
              onClick={handleSubmit}
            >
              <Banknote className="h-6 w-6 text-primary" />
              <div className="flex flex-1 flex-col items-start">
                <span className="text-lg font-semibold text-black01 sm:text-xl">
                  {isCreatingCustomer || isCreatingTransaction
                    ? "Iniciando saque..."
                    : "Iniciar saque"}
                </span>
                <p className="text-xs font-medium text-black03 opacity-80">
                  Liberação <span className="font-semibold">em até 24h</span>
                </p>
              </div>
              <ArrowRight className="ml-auto h-5 w-5 text-primary" />
            </button>
          </div>

          <button
            className="hover:bg-white03 w-full rounded-xl bg-gray02 py-4 text-lg font-semibold text-black01 transition-colors"
            onClick={onBack}
          >
            Voltar
          </button>
        </div>
      </div>
    </div>
  );
}
